import React from "react";
import { CHAT_INPUT_MEASURE } from "./AiChatInput";
import goatImg1 from "../../../assets/images/goat-no-background.png";
import goatImg2 from "../../../assets/images/goat-no-background-2.png";
import goatImg3 from "../../../assets/images/goat-no-background-3.png";
// This component displays the empty state of the AI chat panel, with different variants and interactive prompts.

// Define the variant types for the empty state
type AiChatEmptyStateVariant = "panel" | "analyst";

// Props definition for AiChatEmptyState component
interface AiChatEmptyStateProps {
  variant?: AiChatEmptyStateVariant;
  pinnedPrompts?: string[];
  onPromptSelect?: (prompt: string) => void;
  /** Rendered under the heading (analyst variant). */
  inputSlot?: React.ReactNode;
}

// AiChatEmptyState component definition
const AiChatEmptyState: React.FC<AiChatEmptyStateProps> = ({
  variant = "panel",
  pinnedPrompts = [],
  onPromptSelect,
  inputSlot,
}) => {
  // Array of goat images to randomly choose from
  const goatImages = [goatImg1, goatImg2, goatImg3];
  
  // Randomly select one of the goat images
  const randomGoatImage = React.useMemo(() => {
    return goatImages[Math.floor(Math.random() * goatImages.length)];
  }, []);

  // Render the analyst variant of the empty state
  if (variant === "analyst") {
    return (
      <div className="flex h-full w-full flex-col text-center">
        {/* m-auto not justify-center — degrades to top-aligned/scrollable instead of clipping unreachably when the group outgrows the viewport */}
        <div className="m-auto flex w-full flex-col items-center">
          <div className="mb-4 sm:mb-6 relative h-32 sm:h-40">
            <img
              src={randomGoatImage}
              alt="AI Analyst Goat"
              className="h-full w-auto rounded-eai-card object-contain"
            />
            {/* Vignette fades the image edges into the chat ground — must track eai-surface exactly or it rings */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(circle, transparent 50%, rgba(246,247,245,0.2) 70%, rgba(246,247,245,0.6) 85%)'
            }} />
          </div>

          <h2 className="font-outfit text-eai-page-title font-semibold tracking-tight text-eai-ink sm:text-eai-page-title-lg">
            How can I help you today?
          </h2>
          <p className="eai-description-sm mt-2 max-w-[560px] sm:text-eai-body">
            Ask me anything about water, weather, or energy.
          </p>
          {inputSlot && (
            /* min-h mirrors AiChatInput's one-line height (padding + textarea + button row) so the goat doesn't jump when it mounts — resize with it */
            <div className={`relative mt-6 min-h-[84px] w-full text-left sm:min-h-[102px] ${CHAT_INPUT_MEASURE}`}>
              {/* Bottom-anchored so a long message grows the box upward over the goat, keeping the send/attach buttons fixed */}
              <div className="absolute inset-x-0 bottom-0 z-10">{inputSlot}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render the panel variant of the empty state
  return (
    <>
      <div className="flex flex-col items-center px-6 pb-6 pt-6 text-center">
        <h2 className="font-outfit mt-6 text-lg font-semibold text-eai-ink">
          How can I help you today?
        </h2>
        <p className="font-inter mt-1 text-xs text-eai-ink-subtle">
          Ask questions about your assets
        </p>
      </div>
      <div className="flex flex-col gap-2 px-3 pb-6">
        <span className="eai-eyebrow px-1">Get started with an example</span>
        {pinnedPrompts.map((prompt) => (
          /* A real button, so Enter/Space and focus come from the platform rather than a keydown handler */
          <button
            key={prompt}
            type="button"
            onClick={() => onPromptSelect?.(prompt)}
            className="eai-card-interactive font-inter w-full px-3 py-2.5 text-left text-eai-body-sm text-eai-ink"
          >
            {prompt}
          </button>
        ))}
      </div>
    </>
  );
};

export default AiChatEmptyState;